import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useCategories } from "@/hooks/useCategories";
import { useAccounts } from "@/hooks/useAccounts";
import { useSavings } from "@/hooks/useSavings";
import { useInstallments } from "@/hooks/useInstallments";
import { useSavingPockets } from "@/hooks/useSavingPockets";
import { useTags } from "@/hooks/useTags";
import { localTodayYMD, toDateInputValue } from "@/utils/dateUtils";
import { FiAlertCircle, FiCheck, FiPaperclip, FiTrash2 } from "react-icons/fi";
import { ModalShell } from "@/components/ui/ModalShell.tsx";
import { AmountField } from "@/components/ui/AmountField";
import {
  AppButton,
  FormField,
  MessageBanner,
  ModalActions,
  SelectInput,
  TextArea,
  TextInput,
} from "@/components/ui/primitives";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { usePreferencesStore } from "@/stores/usePreferencesStore";
import type { Installment } from "@/hooks/useInstallments";

interface TransactionData {
  name: string;
  amount: string;
  category: string;
  tag_id?: number;
  date: string;
  account: string;
  destination_account?: string;
  type: string;
  description: string;
  ticket_img?: string | null;
  saving_plan_id?: number | null;
  saving_pocket_id?: number | null;
  installment_id?: number | null;
}

interface NewTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (transactionData: TransactionData) => void | Promise<void>;
  initialData?: Partial<TransactionData>;
  isEditMode?: boolean;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
  /** Edit mode only: shows a destructive «Eliminar» on the left of the footer. */
  onDelete?: () => void;
  deleteLabel?: string;
}

const MAX_RECEIPT_DIMENSION = 1080;
const RECEIPT_IMAGE_QUALITY = 0.72;

type FieldErrorKey = "amount" | "name" | "date" | "account" | "destination_account";
type FieldErrors = Partial<Record<FieldErrorKey, string>>;

/** Input ids, in DOM order: the first invalid one receives focus on submit. */
const FIELD_INPUT_IDS: Record<FieldErrorKey, string> = {
  amount: "transaction-amount",
  name: "transaction-name",
  date: "transaction-date",
  account: "transaction-account",
  destination_account: "transaction-destination-account",
};
const FIELD_FOCUS_ORDER: FieldErrorKey[] = ["amount", "name", "date", "account", "destination_account"];
const errorIdFor = (key: FieldErrorKey) => `${FIELD_INPUT_IDS[key]}-error`;

// eslint-disable-next-line react-refresh/only-export-components
export const getAvailableInstallments = (
  installments: Installment[],
  selectedInstallmentId?: number | null,
) =>
  installments
    .filter(
      (installment) =>
        installment.id === selectedInstallmentId ||
        (installment.completed !== true &&
          (installment.num_payments == null || installment.done_payments < installment.num_payments)),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

const formatFileSize = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)}MB`;

const readFileAsDataUrl = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load image"));
    image.src = src;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Could not generate compressed image"));
        return;
      }
      resolve(blob);
    }, type, quality);
  });

const compressReceiptImage = async (file: File) => {
  const sourceDataUrl = await readFileAsDataUrl(file);
  const image = await loadImage(sourceDataUrl);
  const longestSide = Math.max(image.width, image.height);
  const scale = longestSide > MAX_RECEIPT_DIMENSION ? MAX_RECEIPT_DIMENSION / longestSide : 1;
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext("2d");
  if (!context) {
    return { base64: sourceDataUrl, sizeLabel: formatFileSize(file.size) };
  }

  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  const compressedBlob = await canvasToBlob(canvas, "image/jpeg", RECEIPT_IMAGE_QUALITY);

  if (compressedBlob.size >= file.size) {
    return { base64: sourceDataUrl, sizeLabel: formatFileSize(file.size) };
  }

  return {
    base64: await readFileAsDataUrl(compressedBlob),
    sizeLabel: formatFileSize(compressedBlob.size),
  };
};

export const NewTransactionModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isEditMode = false,
  title,
  subtitle,
  submitLabel,
  onDelete,
  deleteLabel = "Eliminar",
}: NewTransactionModalProps) => {
  const { t } = useTranslation(["transactionModal", "common"]);
  const appMode = usePreferencesStore((state) => state.appMode);
  const isAdvancedMode = appMode === "advanced";
  const { data: categories = [] } = useCategories({ enabled: isOpen });
  const { data: accounts = [], isLoading: isLoadingAccounts } = useAccounts({ enabled: isOpen });
  const { data: tags = [], isLoading: isLoadingTags } = useTags({ enabled: isOpen });
  const { data: savings, isLoading: isLoadingSavings } = useSavings(undefined, { enabled: isOpen && isAdvancedMode });
  const { data: savingPockets = [], isLoading: isLoadingSavingPockets } = useSavingPockets({
    enabled: isOpen && isAdvancedMode && FEATURE_FLAGS.savingPockets,
  });
  const { data: installments = [], isLoading: isLoadingInstallments } = useInstallments({ enabled: isOpen && isAdvancedMode });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const bodyScrollYRef = useRef(0);
  const bodyStyleRef = useRef({
    position: "",
    top: "",
    width: "",
    overflow: "",
  });

  useLayoutEffect(() => {
    if (!isOpen) return;

    const body = document.body;
    bodyScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    bodyStyleRef.current = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = "fixed";
    body.style.top = `-${bodyScrollYRef.current}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";

    const resetScroll = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
        scrollContainerRef.current.scrollTo({ top: 0 });
      }
    };

    resetScroll();
    requestAnimationFrame(() => {
      resetScroll();

      if (window.matchMedia("(min-width: 768px)").matches) {
        nameInputRef.current?.focus({ preventScroll: true });
      }
    });
    const timeoutId = window.setTimeout(resetScroll, 0);

    return () => {
      window.clearTimeout(timeoutId);
      body.style.position = bodyStyleRef.current.position;
      body.style.top = bodyStyleRef.current.top;
      body.style.width = bodyStyleRef.current.width;
      body.style.overflow = bodyStyleRef.current.overflow;
      window.scrollTo(0, bodyScrollYRef.current);
    };
  }, [isOpen]);

  // ...existing code for getInitialFormData, formData, uploadedFiles states...

  const getInitialFormData = (): TransactionData => {
    if (initialData) {
      return {
        name: initialData.name || "",
        amount: initialData.amount || "",
        category: initialData.category || "",
        tag_id: initialData.tag_id ?? undefined,
        date: toDateInputValue(initialData.date) || localTodayYMD(),
        account: initialData.account || "",
        destination_account: initialData.destination_account || "",
        type: initialData.type || "expense",
        description: initialData.description || "",
        ticket_img: initialData.ticket_img,
        saving_plan_id: initialData.saving_plan_id ?? undefined,
        saving_pocket_id: FEATURE_FLAGS.savingPockets ? (initialData.saving_pocket_id ?? undefined) : undefined,
        installment_id: initialData.installment_id ?? undefined,
      };
    }
    return {
      name: "",
      amount: "",
      category: "",
      tag_id: undefined,
      date: localTodayYMD(),
      account: "",
      destination_account: "",
      type: "expense",
      description: "",
      saving_plan_id: undefined,
      saving_pocket_id: undefined,
      installment_id: undefined,
    };
  };

  const [formData, setFormData] = useState<TransactionData>(getInitialFormData());

  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{ name: string; size: string; progress: number; complete: boolean; base64?: string }>
  >([]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  // Usar una referencia para rastrear el estado anterior de isOpen
  const prevIsOpenRef = useRef(isOpen);

  // Actualizar formData solo cuando el modal se abre por primera vez (transición de false a true)
  useEffect(() => {
    // Solo reiniciar el formulario cuando el modal se abre (transición de cerrado a abierto)
    if (!prevIsOpenRef.current && isOpen) {
      setFormData(getInitialFormData());
      setFormError(null);
      setFieldErrors({});
      // Si hay ticket_img en initialData, agregarlo a uploadedFiles
      if (initialData?.ticket_img) {
        setUploadedFiles([
          {
            name: "receipt.jpg",
            size: `${(initialData.ticket_img.length / 1024 / 1024).toFixed(1)}MB`,
            progress: 100,
            complete: true,
            base64: initialData.ticket_img,
          },
        ]);
      } else {
        setUploadedFiles([]);
      }
    }

    // Actualizar la referencia para el próximo render
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  const resetForm = () => {
    setFormData({
      name: "",
      amount: "",
      category: "",
      tag_id: undefined,
      date: localTodayYMD(),
      account: "",
      destination_account: "",
      type: "expense",
      description: "",
      saving_plan_id: undefined,
      saving_pocket_id: undefined,
      installment_id: undefined,
    });
    setUploadedFiles([]);
    setFormError(null);
    setFieldErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleInputChange = (field: string, value: string | boolean | number | null | undefined) => {
    setFormError(null);
    setFieldErrors((prev) => ({ ...prev, [field as FieldErrorKey]: undefined }));
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const transactionTypeOptions = [
    { value: "expense", label: t("transactions.activityTypeOutflow") },
    { value: "deposit", label: t("transactions.activityTypeInflow") },
    { value: "transfer", label: t("transactions.activityTypeTransfer") },
  ] as const;
  const transactionTypeSelector = (
    <div id="transaction-type" role="group" aria-labelledby="transaction-type-label" className="grid min-h-[54px] grid-cols-3 rounded-full border border-[var(--ds-line)] bg-[var(--ds-surface)] p-1">
      {transactionTypeOptions.map((option, index) => {
        const isActive = formData.type === option.value;

        return (
          <button
            id={index === 0 ? "transaction-type-expense" : undefined}
            key={option.value}
            type="button"
            onClick={() => handleInputChange("type", option.value)}
            aria-pressed={isActive}
            className={`app-focus-ring min-h-11 rounded-full px-1 text-[13px] font-medium transition-colors ${
              isActive
                ? "bg-[var(--ds-card-sun)] text-[var(--ds-on-card)]"
                : "text-[var(--ds-ink)] hover:bg-[var(--ds-ground)]"
            }`}
            disabled={isSubmitting}
          >
            {option.value === "transfer" ? (
              <>
                <span className="sm:hidden">Transf.</span>
                <span className="hidden sm:inline">Transferencia</span>
              </>
            ) : (
              option.label
            )}
          </button>
        );
      })}
    </div>
  );

  const isTransfer = formData.type?.toLowerCase() === "transfer";
  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts],
  );
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );
  const sortedTags = useMemo(
    () => [...tags].sort((a, b) => a.name.localeCompare(b.name)),
    [tags],
  );
  const sortedSavings = useMemo(
    () => [...(savings?.saving_plans_balance ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [savings?.saving_plans_balance],
  );
  const sortedSavingPocketOptions = useMemo(
    () => [...savingPockets].sort((a, b) => a.name.localeCompare(b.name)),
    [savingPockets],
  );
  const sortedInstallments = useMemo(
    () => getAvailableInstallments(installments, isEditMode ? initialData?.installment_id : undefined),
    [initialData?.installment_id, installments, isEditMode],
  );
  const destinationAccounts = useMemo(
    () => sortedAccounts.filter((account) => account.name !== formData.account),
    [formData.account, sortedAccounts],
  );

  useEffect(() => {
    if (!isTransfer && formData.destination_account) {
      setFormData((prev) => ({ ...prev, destination_account: "" }));
    }
  }, [isTransfer, formData.destination_account]);

  useEffect(() => {
    if (
      formData.destination_account &&
      formData.destination_account === formData.account
    ) {
      setFormData((prev) => ({ ...prev, destination_account: "" }));
    }
  }, [formData.account, formData.destination_account]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Solo tomar el primer archivo
      const file = files[0];
      setUploadedFiles([
        {
          name: file.name,
          size: formatFileSize(file.size),
          progress: 0,
          complete: false,
        },
      ]);

      const processFile = async () => {
        try {
          const isCompressibleImage = file.type.startsWith("image/");
          const processedFile = isCompressibleImage
            ? await compressReceiptImage(file)
            : {
                base64: await readFileAsDataUrl(file),
                sizeLabel: formatFileSize(file.size),
              };

          setUploadedFiles([
            {
              name: file.name,
              size: processedFile.sizeLabel,
              progress: 100,
              complete: true,
              base64: processedFile.base64,
            },
          ]);
        } catch (error) {
          console.error("Failed to process receipt file", error);
          setFormError(t("transactionModal.failedProcessFile"));
          setUploadedFiles([]);
        } finally {
          event.target.value = "";
        }
      };

      void processFile();
    }
  };

  const removeFile = (fileName: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const validateForm = () => {
    const errors: FieldErrors = {};
    if (!formData.name.trim()) {
      errors.name = t("transactionModal.errors.nameRequired", { defaultValue: "Ponle un nombre" });
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = t("transactionModal.errors.amountRequired", { defaultValue: "Agrega un monto" });
    }
    if (!formData.account) {
      errors.account = isTransfer ? t("transactionModal.errors.sourceRequired", { defaultValue: "Elige de dónde sale" }) : t("transactionModal.errors.accountRequired", { defaultValue: "Elige una cuenta" });
    }
    if (isTransfer) {
      if (!formData.destination_account) {
        errors.destination_account = t("transactionModal.errors.destinationRequired", { defaultValue: "Elige a dónde entra" });
      } else if (formData.destination_account === formData.account) {
        errors.destination_account = t("transactionModal.errors.differentAccounts", { defaultValue: "Elige cuentas diferentes" });
      }
    }
    if (!formData.date) {
      errors.date = t("transactionModal.errors.dateRequired", { defaultValue: "Elige una fecha" });
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    setFieldErrors(errors);
    const firstInvalid = FIELD_FOCUS_ORDER.find((key) => errors[key]);
    if (firstInvalid) {
      window.requestAnimationFrame(() => document.getElementById(FIELD_INPUT_IDS[firstInvalid])?.focus());
      return;
    }

    setFormError(null);
    setIsSubmitting(true);

    try {
      const ticketImage =
        uploadedFiles.length > 0 && uploadedFiles[0].base64 ? uploadedFiles[0].base64 : undefined;

      const dataToSubmit: TransactionData = { ...formData };
      if (!FEATURE_FLAGS.savingPockets) {
        delete dataToSubmit.saving_pocket_id;
      }
      if (!isEditMode && ticketImage) {
        dataToSubmit.ticket_img = ticketImage;
      } else if (isEditMode) {
        if (ticketImage && ticketImage !== initialData?.ticket_img) {
          dataToSubmit.ticket_img = ticketImage;
        } else if (!ticketImage && initialData?.ticket_img) {
          dataToSubmit.ticket_img = null;
        } else {
          delete dataToSubmit.ticket_img;
        }
      }

      if (dataToSubmit.saving_plan_id === undefined) {
        delete dataToSubmit.saving_plan_id;
      }
      if (dataToSubmit.saving_pocket_id === undefined) {
        delete dataToSubmit.saving_pocket_id;
      }
      if (dataToSubmit.installment_id === undefined) {
        delete dataToSubmit.installment_id;
      }
      if (dataToSubmit.tag_id === undefined) {
        delete dataToSubmit.tag_id;
      }

      await onSubmit(dataToSubmit);
      resetForm();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const sectionTitleClassName = "mt-4 text-xs font-medium uppercase tracking-[0.08em] text-[var(--ds-ink-muted)] first:mt-0";
  const gridClassName = "grid grid-cols-1 gap-4 md:grid-cols-2";
  const fullColumnClassName = "md:col-span-2";

  const fieldLabel = (label: React.ReactNode, hint: "required" | "optional") => (
    <span className="flex items-baseline justify-between gap-3">
      <span>{label}</span>
      <span className={`text-xs font-normal ${hint === "required" ? "text-[var(--app-text)]" : "text-[var(--app-muted-text)]"}`}>
        {hint === "required" ? t("transactionModal.hints.required") : t("transactionModal.hints.optional")}
      </span>
    </span>
  );
  const errorText = (key: FieldErrorKey) =>
    fieldErrors[key] ? (
      <span className="flex items-center gap-1">
        <FiAlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        {fieldErrors[key]}
      </span>
    ) : undefined;
  const invalidAttrs = (key: FieldErrorKey) => ({
    "aria-invalid": fieldErrors[key] ? true : undefined,
    "aria-describedby": fieldErrors[key] ? errorIdFor(key) : undefined,
  });

  if (!isOpen) return null;

  const submitButtonLabel = submitLabel ?? (isEditMode ? "Actualizar movimiento" : "Crear movimiento");
  const actions = (
    <ModalActions
      cancelLabel="Cancelar"
      submitLabel={submitButtonLabel}
      onCancel={handleClose}
      isSubmitting={isSubmitting}
    />
  );

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy="modal-title"
      title={title ?? (isEditMode ? "Editar movimiento" : "Nuevo movimiento")}
      subtitle={subtitle ?? "Registra un ingreso, gasto o transferencia."}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      noValidate
      disableClose={isSubmitting}
      contentRef={scrollContainerRef}
      mobileSheet
      maxWidthClassName="max-w-[640px]"
      variant="creation"
      footer={
        isEditMode && onDelete ? (
          <div className="flex w-full items-center justify-between gap-3">
            <AppButton
              type="button"
              tone="dangerSoft"
              onClick={onDelete}
              disabled={isSubmitting}
              aria-label={deleteLabel}
              leadingIcon={<FiTrash2 className="h-4 w-4" aria-hidden="true" />}
              className="shrink-0 px-3 sm:px-4"
            >
              <span className="hidden sm:inline">{deleteLabel}</span>
            </AppButton>
            <div className="min-w-0 flex-1 sm:flex-none">{actions}</div>
          </div>
        ) : (
          actions
        )
      }
    >
      <div className="app-creation-form mx-auto w-full max-w-xl">
        {formError ? (
          <MessageBanner className="app-creation-form-message" tone="error" role="alert">
            {formError}
          </MessageBanner>
        ) : null}

        <h3 className={sectionTitleClassName}>{t("transactionModal.sections.principal")}</h3>

        <div className={gridClassName}>
          <FormField
            className={fullColumnClassName}
            label={fieldLabel(<span id="transaction-type-label">{t("selectTransactionType")}</span>, "required")}
            htmlFor="transaction-type-expense"
          >
            {transactionTypeSelector}
          </FormField>

          <FormField
            className={fullColumnClassName}
            label={fieldLabel("Monto", "required")}
            htmlFor="transaction-amount"
            error={errorText("amount")}
            errorId={errorIdFor("amount")}
          >
            <AmountField
              id="transaction-amount"
              value={formData.amount}
              onChange={(value) => handleInputChange("amount", value)}
              centsLabel={t("transactionModal.amountCents")}
              invalid={Boolean(fieldErrors.amount)}
              describedBy={fieldErrors.amount ? errorIdFor("amount") : undefined}
              required
              disabled={isSubmitting}
            />
          </FormField>

          <FormField
            className={fullColumnClassName}
            label={fieldLabel("Nombre", "required")}
            htmlFor="transaction-name"
            error={errorText("name")}
            errorId={errorIdFor("name")}
          >
            <TextInput
              id="transaction-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Ej. Gimnasio"
              required
              disabled={isSubmitting}
              ref={nameInputRef}
              {...invalidAttrs("name")}
            />
          </FormField>

          <FormField
            label={fieldLabel("Fecha", "required")}
            htmlFor="transaction-date"
            error={errorText("date")}
            errorId={errorIdFor("date")}
          >
            <TextInput
              id="transaction-date"
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange("date", e.target.value)}
              required
              disabled={isSubmitting}
              {...invalidAttrs("date")}
            />
          </FormField>

          <FormField
            label={fieldLabel(isTransfer ? "Sale de" : "Cuenta", "required")}
            htmlFor="transaction-account"
            error={errorText("account")}
            errorId={errorIdFor("account")}
          >
            <SelectInput
              id="transaction-account"
              value={formData.account}
              onChange={(e) => handleInputChange("account", e.target.value)}
              required
              disabled={isSubmitting || isLoadingAccounts}
              {...invalidAttrs("account")}
            >
              <option value="">Seleccionar</option>
              {sortedAccounts.map((account) => (
                <option key={account.id} value={account.name}>
                  {account.name}
                </option>
              ))}
            </SelectInput>
          </FormField>

          {isTransfer ? (
            <FormField
              label={fieldLabel("Entra a", "required")}
              htmlFor="transaction-destination-account"
              error={errorText("destination_account")}
              errorId={errorIdFor("destination_account")}
            >
              <SelectInput
                id="transaction-destination-account"
                value={formData.destination_account || ""}
                onChange={(e) => handleInputChange("destination_account", e.target.value)}
                required
                disabled={isSubmitting || isLoadingAccounts}
                {...invalidAttrs("destination_account")}
              >
                <option value="">Seleccionar</option>
                {destinationAccounts.map((account) => (
                  <option key={account.id} value={account.name}>
                    {account.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          ) : null}
        </div>

        <h3 className={sectionTitleClassName}>{t("transactionModal.sections.context")}</h3>

        <div className={gridClassName}>
          {!isTransfer ? (
            <FormField label={fieldLabel("Categoría", "optional")} htmlFor="transaction-category">
              <SelectInput
                id="transaction-category"
                value={formData.category}
                onChange={(e) => handleInputChange("category", e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Seleccionar</option>
                {sortedCategories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          ) : null}

          {isAdvancedMode ? (
            <FormField label={fieldLabel("Tag", "optional")} htmlFor="transaction-tag">
              <SelectInput
                id="transaction-tag"
                value={formData.tag_id ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange("tag_id", value ? Number(value) : undefined);
                }}
                disabled={isSubmitting || isLoadingTags}
              >
                <option value="">Seleccionar</option>
                {sortedTags.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          ) : null}

          {isAdvancedMode ? (
            <FormField label={fieldLabel("Meta de ahorro", "optional")} htmlFor="saving-plan">
              <SelectInput
                id="saving-plan"
                value={formData.saving_plan_id === null ? "unlink" : (formData.saving_plan_id ?? "")}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "unlink") {
                    handleInputChange("saving_plan_id", null);
                    handleInputChange("installment_id", undefined);
                    return;
                  }
                  const numeric = value ? Number(value) : undefined;
                  handleInputChange("saving_plan_id", numeric);
                  if (numeric) handleInputChange("installment_id", undefined);
                }}
                disabled={isSubmitting || isLoadingSavings}
              >
                <option value="">Seleccionar</option>
                <option value="unlink">Sin meta</option>
                {sortedSavings.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          ) : null}

          {isAdvancedMode && FEATURE_FLAGS.savingPockets ? (
            <FormField label={fieldLabel("Fondo de ahorro", "optional")} htmlFor="saving-pocket">
              <SelectInput
                id="saving-pocket"
                value={formData.saving_pocket_id === null ? "unlink" : (formData.saving_pocket_id ?? "")}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "unlink") {
                    handleInputChange("saving_pocket_id", null);
                    return;
                  }
                  const numeric = value ? Number(value) : undefined;
                  handleInputChange("saving_pocket_id", numeric);
                }}
                disabled={isSubmitting || isLoadingSavingPockets}
              >
                <option value="">Seleccionar</option>
                <option value="unlink">Sin saving pocket</option>
                {sortedSavingPocketOptions.map((pocket) => (
                  <option key={pocket.id} value={pocket.id}>
                    {pocket.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          ) : null}

          {isAdvancedMode ? (
            <FormField label={fieldLabel("Cuota", "optional")} htmlFor="installment-payment">
              <SelectInput
                id="installment-payment"
                value={formData.installment_id === null ? "unlink" : (formData.installment_id ?? "")}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "unlink") {
                    handleInputChange("installment_id", null);
                    handleInputChange("saving_plan_id", undefined);
                    return;
                  }
                  const numeric = value ? Number(value) : undefined;
                  handleInputChange("installment_id", numeric);
                  if (numeric) handleInputChange("saving_plan_id", undefined);
                }}
                disabled={isSubmitting || isLoadingInstallments}
              >
                <option value="">Seleccionar</option>
                <option value="unlink">Sin cuota</option>
                {sortedInstallments.map((installment) => (
                  <option key={installment.id} value={installment.id}>
                    {installment.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          ) : null}
        </div>

        <h3 className={sectionTitleClassName}>{t("transactionModal.sections.optional")}</h3>

        <div className={gridClassName}>
          <FormField className={fullColumnClassName} label={fieldLabel("Comprobante", "optional")} htmlFor="transaction-receipt">
            <div className="md:flex md:items-center md:justify-between md:gap-4">
              <input
                type="file"
                id="transaction-receipt"
                onChange={handleFileUpload}
                className="hidden"
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                disabled={isSubmitting}
              />
              <AppButton
                type="button"
                onClick={() => document.getElementById("transaction-receipt")?.click()}
                tone="secondary"
                size="sm"
                leadingIcon={<FiPaperclip className="hidden h-3.5 w-3.5 md:block" aria-hidden="true" />}
                className="min-h-11 rounded-full px-4 text-sm"
                disabled={isSubmitting}
              >
                Adjuntar
              </AppButton>
              <p className="mt-2 hidden text-[11px] text-[var(--app-muted-text)] md:mt-0 md:text-right">JPG, PNG, PDF o DOC · máx. 30 MB</p>
            </div>

            {uploadedFiles.length > 0 ? (
              <div className="mt-2 space-y-2">
                {uploadedFiles.map((file) => (
                  <div
                    key={file.name}
                    className="flex items-center justify-between rounded-xl border border-[color:color-mix(in_srgb,var(--app-theme-select-border)_72%,var(--app-border)_28%)] bg-[color:color-mix(in_srgb,var(--app-surface-soft)_90%,transparent)] px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {file.complete ? (
                        <FiCheck className="h-4 w-4 shrink-0 text-[var(--app-primary)]" />
                      ) : (
                        <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[var(--app-primary)] border-t-transparent" />
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-[var(--app-text)]">{file.name}</p>
                        <p className="text-[11px] text-[var(--app-text-secondary)]">{file.size}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(file.name)}
                      aria-label={t("transactionModal.removeAttachment", { name: file.name })}
                      className="app-focus-ring inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--app-muted-text)] transition hover:bg-[var(--app-surface-hover)] hover:text-[var(--app-danger)]"
                      disabled={isSubmitting}
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </FormField>

          <FormField className={fullColumnClassName} label={fieldLabel("Descripción", "optional")} htmlFor="transaction-notes">
            <TextArea
              id="transaction-notes"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Nota breve"
              rows={3}
              disabled={isSubmitting}
            />
          </FormField>
        </div>
      </div>
    </ModalShell>
  );
};
